import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Properties for the Autostereogram component
 */
interface AutostereogramProps {
  /**
   * Width of the autostereogram in pixels
   */
  width: number;
  /**
   * Height of the autostereogram in pixels
   */
  height: number;
  /**
   * Depth of the 3D effect (0-255)
   */
  depth?: number;
  /**
   * Size of the square in the middle as a fraction of the smallest dimension
   */
  squareSize?: number;
  /**
   * Pattern repeat separation in pixels
   */
  separation?: number;
  /**
   * Random seed for pattern generation
   */
  seed?: number;
}

/**
 * Autostereogram component that generates a "Magic Eye" style image with a square
 * that appears when viewed with defocused eyes.
 */
export const Autostereogram: React.FC<AutostereogramProps> = ({
  width,
  height,
  depth = 1,
  squareSize = 0.4,
  separation = 15,
  seed = 42,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Creates a depth map with a square in the middle
   * @param width Width of the depth map
   * @param height Height of the depth map
   * @param squareSizeFactor Size of the square as a fraction of the smallest dimension
   * @returns A 2D array representing the depth map (0-255)
   */
  const createDepthMap = (width: number, height: number, squareSizeFactor: number): number[][] => {
    const depthMap: number[][] = Array(height)
      .fill(0)
      .map(() => Array(width).fill(0));

    const minDimension = Math.min(width, height);
    const squareSize = Math.floor(minDimension * squareSizeFactor);
    const startX = Math.floor((width - squareSize) / 2);
    const startY = Math.floor((height - squareSize) / 2);

    // Create a square in the middle
    for (let y = startY; y < startY + squareSize; y++) {
      for (let x = startX; x < startX + squareSize; x++) {
        if (y >= 0 && y < height && x >= 0 && x < width) {
          depthMap[y][x] = 255; // Full depth for the square
        }
      }
    }

    return depthMap;
  };

  /**
   * Generates a random pattern for the autostereogram with vertical variation
   * @param width Width of the pattern
   * @param height Height of the image for vertical variation
   * @param initialSeed Random seed for reproducibility
   * @returns A 2D array of RGB values for each pixel in the pattern
   */
  const generatePattern = (width: number, height: number, initialSeed: number): Uint8ClampedArray[] => {
    const patterns: Uint8ClampedArray[] = [];

    // Create a different pattern for each row
    for (let y = 0; y < height; y++) {
      // Use a different seed for each row based on the initial seed
      let rowSeed = initialSeed + y * 1000;
      const pattern = new Uint8ClampedArray(width * 4);

      // Simple pseudo-random number generator with seed
      const random = () => {
        rowSeed = (rowSeed * 9301 + 49297) % 233280;
        return rowSeed / 233280;
      };

      for (let i = 0; i < width; i++) {
        const idx = i * 4;
        pattern[idx] = Math.floor(random() * 256); // R
        pattern[idx + 1] = Math.floor(random() * 256); // G
        pattern[idx + 2] = Math.floor(random() * 256); // B
        pattern[idx + 3] = 255; // A (always fully opaque)
      }

      patterns.push(pattern);
    }

    return patterns;
  };

  /**
   * Generates the autostereogram image
   */
  const generateAutostereogram = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Create the depth map with a square in the middle
    const depthMap = createDepthMap(width, height, squareSize);

    // Generate random patterns for each row
    const patterns = generatePattern(separation, height, seed);

    // Create an ImageData object to hold our pixel data
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    // Maximum shift to prevent artifacts
    const maxShift = Math.min(separation / 2, depth);

    // For each row in the image
    for (let y = 0; y < height; y++) {
      const rowPattern = patterns[y];

      // First, copy the random pattern to the first separation pixels of the row
      for (let x = 0; x < separation; x++) {
        const idx = (y * width + x) * 4;
        data[idx] = rowPattern[x * 4];
        data[idx + 1] = rowPattern[x * 4 + 1];
        data[idx + 2] = rowPattern[x * 4 + 2];
        data[idx + 3] = 255;
      }

      // Then, for each remaining pixel in the row
      for (let x = separation; x < width; x++) {
        const depthValue = depthMap[y][x];

        // Calculate shift based on depth with a limit to prevent artifacts
        const shift = Math.floor((depthValue * maxShift) / 255);

        // Calculate which pixel to copy from
        const sameAsX = x - separation + shift;

        // Ensure source pixel is within bounds
        const sourceX = Math.max(0, Math.min(x - 1, sameAsX));

        // Get the source index
        const sourceIdx = (y * width + sourceX) * 4;
        const destIdx = (y * width + x) * 4;

        // Copy the color from the source position
        data[destIdx] = data[sourceIdx];
        data[destIdx + 1] = data[sourceIdx + 1];
        data[destIdx + 2] = data[sourceIdx + 2];
        data[destIdx + 3] = 255;
      }
    }

    // Put the image data onto the canvas
    ctx.putImageData(imageData, 0, 0);
  }, [width, height, depth, squareSize, separation, seed]);

  // Generate the autostereogram when the component mounts or props change
  useEffect(() => {
    generateAutostereogram();
  }, [generateAutostereogram]);

  return (
    <div>
      <canvas ref={canvasRef} width={width} height={height} style={{ border: '1px solid #ccc' }} />
      <p style={{ textAlign: 'center', marginTop: '10px' }}>Focus your eyes behind the image to see the 3D effect</p>
    </div>
  );
};

export default Autostereogram;
